import { useState } from 'react';
import { Form, Link } from 'react-router';
import { AuthenticityTokenInput } from 'remix-utils/csrf/react';

import { Logo } from '~/components/logos/logo';
import { AppDrawer } from '~/components/nav/app-drawer';
import { Icon } from '~/components/ui/icon';
import { UserAvatarMenu } from '~/components/user/user-avatar-menu';
import { type Permission } from '~/domain/users/models/permission';
import { cn } from '~/styles/extended-theme';
import { type PropsWithClassName } from '~/utils/props-with-classname';

import { UserAvatarProfileLink } from '../user/user-avatar-profile-link';

import { MobileNavLinks } from './mobile-nav-links';
import { NavbarLinks } from './navbar-links';

interface Props {
  email: string;
  displayName: string;
  avatarUrl?: string | null;
  title?: string;
  fullWidth?: boolean;
  userPermissions: Permission[];
  userGymIds: string[];
  athleteProfileId?: string;
}

export function Navbar({
  avatarUrl,
  displayName,
  email,
  userPermissions,
  userGymIds,
  className,
  athleteProfileId,
}: PropsWithClassName<Props>) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  return (
    <>
      {/* phone drawer */}
      <AppDrawer open={isDrawerOpen}>
        <nav className="flex flex-col gap-4">
          <UserAvatarProfileLink
            avatarUrl={avatarUrl}
            email={email}
            displayName={displayName}
            color="light"
            size="lg"
          />
          <div className="flex flex-col gap-2">
            <MobileNavLinks
              userPermissions={userPermissions}
              userGymIds={userGymIds}
              athleteProfileId={athleteProfileId}
            />

            <hr className="mt-2 border-primary-foreground pt-2" />
            <Form action="/logout" method="post">
              <AuthenticityTokenInput />
              <button
                type="submit"
                className="px-2 py-4 font-mono uppercase text-primary-foreground"
              >
                Log Out
              </button>
            </Form>
          </div>
        </nav>
      </AppDrawer>

      <nav
        className={cn(
          'sticky top-0 z-50 w-full rounded-b-2xl border-b-4 border-b-secondary bg-primary px-6 py-2 text-zinc-500 shadow-sm tablet:px-12 tablet:py-0',
          className,
        )}
      >
        {/* phone */}
        <div className="flex items-center gap-8 tablet:hidden">
          <div className="text-primary-foreground">
            {isDrawerOpen ? (
              <>
                <Icon
                  name="x"
                  size="lg"
                  aria-label="Close navigation menu"
                  onClick={() => setIsDrawerOpen(false)}
                />
              </>
            ) : (
              <Icon
                name="hamburger"
                size="lg"
                aria-label="Open navigation menu"
                onClick={() => setIsDrawerOpen(true)}
              />
            )}
          </div>

          <div className="ml-auto flex">
            <Link to="/">
              <Logo size="sm" />
            </Link>
          </div>
        </div>

        {/* tablet and up */}
        <div className={cn('hidden w-full items-center gap-8 tablet:flex')}>
          {/* in case window was resized with drawer open */}
          {isDrawerOpen ? (
            <Icon
              name="x"
              size="lg"
              aria-label="Close navigation menu"
              className="text-primary-foreground"
              onClick={() => setIsDrawerOpen(false)}
            />
          ) : null}
          <Link to="/">
            <Logo size="sm" />
          </Link>

          <NavbarLinks
            userPermissions={userPermissions}
            userGymIds={userGymIds}
            athleteProfileId={athleteProfileId}
          />

          <div className="ml-auto flex">
            <UserAvatarMenu
              email={email ?? 'User'}
              displayName={displayName}
              avatarUrl={avatarUrl}
            />
          </div>
        </div>
      </nav>
    </>
  );
}
